import { Trans, useTranslation } from "react-i18next";
import { Button } from "~/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Text } from "~/components/text";
import { useEmulatorLoginLink } from "~/hooks/auth/use-emulator-login-link";

interface LoginLinkSuccessModalProps {
  email: string;
  open: boolean;
  onClose(): void;
}

export function LoginLinkSuccessModal({
  email,
  open,
  onClose,
}: LoginLinkSuccessModalProps) {
  const { t } = useTranslation("login");
  const emulatorLoginLink = useEmulatorLoginLink(email, open);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="max-w-2xl" showCloseButton={false}>
        <DialogHeader className="gap-y-2 flex flex-col items-center">
          <span className="text-5xl">📨</span>
          <div className="space-y-4 text-center">
            <DialogTitle>{t("link.success.title")}</DialogTitle>
            <DialogDescription className="text-md font-light text-secondary-600 pb-2">
              <Trans
                i18nKey="login:link.success.description"
                values={{ email }}
                components={{
                  strong: (
                    <Text as="span" variant="label" className="break-all" />
                  ),
                }}
              />
            </DialogDescription>
          </div>
        </DialogHeader>

        {emulatorLoginLink && (
          <div className="flex justify-center">
            <a
              href={emulatorLoginLink}
              className="inline-flex items-center gap-2 rounded-md border border-dashed border-amber-400 bg-amber-50 px-4 py-2 text-sm font-medium text-amber-800 hover:bg-amber-100"
            >
              {t("link.success.emulatorLink")}
            </a>
          </div>
        )}

        <DialogFooter className="block text-secondary-600 text-sm font-light border-t border-secondary-200 pt-6 text-center">
          <Trans
            i18nKey="login:link.success.help"
            components={{
              button: (
                <Button
                  type="button"
                  variant="link"
                  onClick={onClose}
                  className="p-0"
                />
              ),
            }}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
